import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Building2, Globe2, Pencil, Plus, Search, Trash2 } from "lucide-react";

import api from "@/lib/api";
import AgencyPartnerForm from "@/components/AgencyPartnerForm";

const titleCase = (value = "") =>
  String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function AgencyPartners() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [specialisation, setSpecialisation] = useState("");
  const [relationshipStage, setRelationshipStage] = useState("");
  const [agreementStatus, setAgreementStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const metaQuery = useQuery({
    queryKey: ["agency-partners-meta"],
    queryFn: async () => (await api.get("/agency-partners/meta")).data,
  });

  const params = useMemo(() => {
    const result = {};
    if (search.trim()) result.search = search.trim();
    if (country) result.country = country;
    if (specialisation) result.specialisation = specialisation;
    if (relationshipStage) result.relationship_stage = relationshipStage;
    if (agreementStatus) result.agreement_status = agreementStatus;
    return result;
  }, [
    search,
    country,
    specialisation,
    relationshipStage,
    agreementStatus,
  ]);

  const partnersQuery = useQuery({
    queryKey: ["agency-partners", params],
    queryFn: async () =>
      (await api.get("/agency-partners", { params })).data,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (editing?.id) {
        return (
          await api.put(`/agency-partners/${editing.id}`, payload)
        ).data;
      }
      return (await api.post("/agency-partners", payload)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-partners"] });
      setFormOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) =>
      (await api.delete(`/agency-partners/${id}`)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["agency-partners"] }),
  });

  const raw = partnersQuery.data;
  const partners = Array.isArray(raw)
    ? raw
    : raw?.items || raw?.results || [];

  const countries = useMemo(
    () =>
      [...new Set(partners.map((p) => p.country).filter(Boolean))]
        .sort(),
    [partners]
  );

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (partner) => {
    setEditing(partner);
    setFormOpen(true);
  };

  const removePartner = (partner) => {
    if (
      window.confirm(
        t(
          "agencyPartners.confirmDelete",
          `Delete ${partner.agency_name || "this agency"}?`
        )
      )
    ) {
      deleteMutation.mutate(partner.id);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t(
              "agencyPartners.eyebrow",
              "International cooperation"
            )}
          </div>

          <h1 className="mt-1 text-3xl font-semibold text-slate-900">
            {t(
              "agencyPartners.title",
              "Agency Partner Network"
            )}
          </h1>

          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            {t(
              "agencyPartners.subtitle",
              "Manage real-estate agencies, geographic coverage, cooperation terms, introductions and commission arrangements."
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate("/agency-partners/discover")}
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium"
          >
            <Globe2 size={17} />
            {t("agencyPartners.findAgencies", "Find agencies")}
          </button>

          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            <Plus size={17} />
            {t("agencyPartners.addAgency", "Add agency")}
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border bg-white p-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="relative">
          <Search
            size={17}
            className="absolute left-3 top-3 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              "agencyPartners.search",
              "Search agency, city, contact…"
            )}
            className="w-full rounded-lg border py-2 pl-9 pr-3"
          />
        </div>

        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="rounded-lg border px-3 py-2"
        >
          <option value="">
            {t("agencyPartners.allCountries", "All countries")}
          </option>
          {countries.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>

        <select
          value={specialisation}
          onChange={(e) => setSpecialisation(e.target.value)}
          className="rounded-lg border px-3 py-2"
        >
          <option value="">
            {t(
              "agencyPartners.allSpecialisations",
              "All specialisations"
            )}
          </option>
          {(metaQuery.data?.property_specialisations || []).map(
            (item) => (
              <option key={item} value={item}>
                {t(
                  `agencyPartners.specialisations.${item}`,
                  titleCase(item)
                )}
              </option>
            )
          )}
        </select>

        <select
          value={relationshipStage}
          onChange={(e) => setRelationshipStage(e.target.value)}
          className="rounded-lg border px-3 py-2"
        >
          <option value="">
            {t(
              "agencyPartners.allRelationships",
              "All relationship stages"
            )}
          </option>
          {(metaQuery.data?.relationship_stages || []).map((item) => (
            <option key={item} value={item}>
              {titleCase(item)}
            </option>
          ))}
        </select>

        <select
          value={agreementStatus}
          onChange={(e) => setAgreementStatus(e.target.value)}
          className="rounded-lg border px-3 py-2"
        >
          <option value="">
            {t(
              "agencyPartners.allAgreements",
              "All agreement statuses"
            )}
          </option>
          {(metaQuery.data?.agreement_statuses || []).map((item) => (
            <option key={item} value={item}>
              {titleCase(item)}
            </option>
          ))}
        </select>
      </div>

      {partnersQuery.isLoading ? (
        <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
          {t("common.loading", "Loading…")}
        </div>
      ) : partners.length === 0 ? (
        <div className="rounded-2xl border bg-white p-10 text-center">
          <Building2
            size={38}
            className="mx-auto mb-3 text-slate-400"
          />
          <h2 className="font-semibold text-slate-900">
            {t(
              "agencyPartners.emptyTitle",
              "No agency partners found"
            )}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {t(
              "agencyPartners.emptyText",
              "Add an agency manually or discover professional agencies on the web."
            )}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {partners.map((partner) => (
            <article
              key={partner.id}
              className="rounded-2xl border bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 size={19} />
                    <h2 className="text-lg font-semibold text-slate-900">
                      {partner.agency_name || "—"}
                    </h2>
                  </div>

                  <div className="mt-1 text-sm text-slate-500">
                    {[partner.city, partner.region, partner.country]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => openEdit(partner)}
                    className="rounded-lg border p-2"
                    title={t("common.edit", "Edit")}
                  >
                    <Pencil size={16} />
                  </button>

                  <button
                    onClick={() => removePartner(partner)}
                    className="rounded-lg border p-2"
                    title={t("common.delete", "Delete")}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <Info
                  label={t(
                    "agencyPartners.relationship",
                    "Relationship"
                  )}
                  value={titleCase(partner.relationship_stage)}
                />
                <Info
                  label={t(
                    "agencyPartners.cooperation",
                    "Cooperation"
                  )}
                  value={titleCase(partner.cooperation_model)}
                />
                <Info
                  label={t(
                    "agencyPartners.agreement",
                    "Agreement"
                  )}
                  value={titleCase(partner.agreement_status)}
                />
              </div>

              <div className="mt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t(
                    "agencyPartners.specialisationsLabel",
                    "Specialisations"
                  )}
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                  {(partner.property_specialisations || [])
                    .slice(0, 6)
                    .map((item) => (
                      <span
                        key={item}
                        className="rounded-full bg-slate-100 px-3 py-1 text-xs"
                      >
                        {t(
                          `agencyPartners.specialisations.${item}`,
                          titleCase(item)
                        )}
                      </span>
                    ))}

                  {!partner.property_specialisations?.length && (
                    <span className="text-sm text-slate-400">—</span>
                  )}
                </div>
              </div>

              <div className="mt-5 grid gap-2 border-t pt-4 text-sm sm:grid-cols-2">
                <div>
                  <span className="text-slate-500">
                    {t("agencyPartners.contact", "Contact")}:
                  </span>{" "}
                  {partner.contact_name || "—"}
                </div>

                <div>
                  <span className="text-slate-500">
                    {t("agencyPartners.email", "Email")}:
                  </span>{" "}
                  {partner.email || "—"}
                </div>

                <div>
                  <span className="text-slate-500">
                    {t("agencyPartners.phone", "Phone")}:
                  </span>{" "}
                  {partner.phone || "—"}
                </div>

                <div>
                  <span className="text-slate-500">
                    {t("agencyPartners.licence", "Licence / AMI")}:
                  </span>{" "}
                  {partner.licence_number || "—"}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <AgencyPartnerForm
        open={formOpen}
        partner={editing}
        meta={metaQuery.data || {}}
        saving={saveMutation.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSave={(payload) => saveMutation.mutate(payload)}
      />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-slate-800">
        {value || "—"}
      </div>
    </div>
  );
}
